/**
 * @file Beneficiaries.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import { useApplicationContext } from "../../contexts/ApplicationContext";

const TYPES = {
  rollover_ira: "ROLLOVER_ACCOUNT",
  roth_ira: "ROTH_IRA",
  traditional_ira: "TRADITIONAL_IRA",
};

const relationships = [
  { text: "Spouse", value: "SPOUSE" },
  { text: "Child", value: "CHILD" },
  { text: "Other", value: "OTHER" },
];

const beneficiaryTypes = [
  { text: "Primary", value: "PRIMARY" },
  { text: "Contingent", value: "CONTINGENT" },
];

function Beneficiaries({ sendBeneficiaries }) {
  const { applicationType } = useApplicationContext();

  const [beneficiaries, setBeneficiaries] = useState([
    {
      name: { givenName: "", familyName: "" },
      address: {
        streetAddress: [""],
        country: "USA",
        city: "",
        state: "",
        postalCode: "",
      },
      dateOfBirth: "",
      socialSecurityNumber: "",
      sharePercent: 100,
      relationship: "",
      beneficiaryType: "",
    },
  ]);

  const handleChange = (index, field, value, nestedField) => {
    setBeneficiaries((prev) =>
      prev.map((ben, i) =>
        i === index
          ? nestedField
            ? {
                ...ben,
                [field]: {
                  ...ben[field],
                  [nestedField]: value,
                },
              }
            : {
                ...ben,
                [field]: value,
              }
          : ben
      )
    );
  };

  const handleStreetChange = (index, value) => {
    setBeneficiaries((prev) =>
      prev.map((ben, i) =>
        i === index
          ? {
              ...ben,
              address: {
                ...ben.address,
                streetAddress: [value],
              },
            }
          : ben
      )
    );
  };

  useEffect(() => {
    sendBeneficiaries({
      retirementAccountBeneficiariesForm: {
        beneficiaries,
      },
    });
  }, [beneficiaries, applicationType]);
  

  return (
    <div>
      <p className="subtitle">Beneficiary</p>
      {beneficiaries.map((beneficiary, index) => (
        <Grid
          container
          rowSpacing={4}
          columnSpacing={{ xs: 0, sm: 2, md: 12 }}
          key={index}
        >
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "First Name",
                  name: "givenName",
                  value: beneficiary.name.givenName,
                  action: (e) =>
                    handleChange(index, "name", e.target.value, "givenName"),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Last Name",
                  name: "familyName",
                  value: beneficiary.name.familyName,
                  action: (e) =>
                    handleChange(index, "name", e.target.value, "familyName"),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Street Address",
                  name: "streetAddress",
                  value: beneficiary.address.streetAddress[0],
                  action: (e) => handleStreetChange(index, e.target.value),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "City",
                  name: "city",
                  value: beneficiary.address.city,
                  action: (e) =>
                    handleChange(index, "address", e.target.value, "city"),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "State",
                  name: "state",
                  value: beneficiary.address.state,
                  action: (e) =>
                    handleChange(index, "address", e.target.value, "state"),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Postal Code",
                  name: "postalCode",
                  value: beneficiary.address.postalCode,
                  action: (e) =>
                    handleChange(index, "address", e.target.value, "postalCode"),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>

          {/* Additional Fields */}
          <Grid item xs={6}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Relationship",
                  name: "relationship",
                  value: beneficiary.relationship,
                  action: (e) =>
                    handleChange(index, "relationship", e.target.value),
                  tooltipBool: false,
                }}
                data={relationships}
              />
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="form-field">
              <SelectInput
                customInput={{
                  label: "Beneficiary Type",
                  name: "beneficiaryType",
                  value: beneficiary.beneficiaryType,
                  action: (e) =>
                    handleChange(index, "beneficiaryType", e.target.value),
                  tooltipBool: false,
                }}
                data={beneficiaryTypes}
              />
            </div>
          </Grid>

          <Grid item xs={12} sm={6}>
            <div className="form-field">
              <label>Date of birth</label>
              <input
                type="date"
                value={beneficiary.dateOfBirth}
                onChange={(e) =>
                  handleChange(index, "dateOfBirth", e.target.value)
                }
              />
            </div>
          </Grid>

          <Grid item xs={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Social Security Number",
                  name: "socialSecurityNumber",
                  value: beneficiary.socialSecurityNumber,
                  action: (e) =>
                    handleChange(index, "socialSecurityNumber", e.target.value),
                  tooltipBool: false,
                  maskType: "ssn",
                  maxLength: 11,
                }}
              />
            </div>
          </Grid>

          <Grid item xs={6}>
            <div className="form-field">
              <TextInput
                customInput={{
                  label: "Share Percentage",
                  name: "sharePercent",
                  value: 100,
                  disabled: true,
                  action: (e) =>
                    handleChange(index, "sharePercent", e.target.value),
                  tooltipBool: false,
                }}
              />
            </div>
          </Grid>
        </Grid>
      ))}
    </div>
  );
}

export default Beneficiaries;
